import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Dropdown } from 'react-bootstrap';
import { ServerPath } from '../../Constant';
import { toast } from 'react-toastify';
// import axios from 'axios';
import cookieUtils from '../../utils/cookieUtils';
import { _axios } from '../../utils/axios';

const CreateChapter = ({ show, onHide, courseId, courseName }) => {
    const containsInvalidCharacters = (text) => {
        // This regex pattern will detect emojis and many special unicode characters
        const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
        
        // Test if the text contains emojis or other invalid characters
        return emojiRegex.test(text);
      };
    // Get user ID from cookies
    const userRole = cookieUtils.getSecureCookie('role');
    const adminId = cookieUtils.getSecureCookie('admin_id');
    const userId = cookieUtils.getSecureCookie('user_id');

    // Use appropriate ID based on user role
    const currentId = userRole === 'superadmin' ? adminId : userId;

    const [chapterTitle, setChapterTitle] = useState('');
    const [selectedChapter, setSelectedChapter] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [chapterOptions, setChapterOptions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState('New');
    const [chapterTitleMar, setChapterTitleMar] = useState('');
    const [chapterTitleHin, setChapterTitleHin] = useState('');
    const [chapterTitleGuj, setChapterTitleGuj] = useState('');

    // Fetch available chapter numbers
    useEffect(() => {
        if (courseId) {
            fetchAvailableChapterNumbers();
        }
    }, [courseId]);

    const fetchAvailableChapterNumbers = async () => {
        try {
            // First get existing chapters to determine what chapter numbers are already used
            const response = await _axios.get(`${ServerPath}/chapter/course/${courseId}`);

            if (response.data.success) {
                const existingChapters = response.data.data || [];
                const usedNumbers = new Set(existingChapters.map(c => c.sequence_number));

                // Create chapter options for the next 10 possible chapters
                const maxSequence = existingChapters.length > 0
                    ? Math.max(...existingChapters.map(c => c.sequence_number))
                    : 0;

                // Generate options for the dropdown
                const options = [];
                for (let i = 1; i <= maxSequence + 10; i++) {
                    options.push({
                        value: i,
                        label: `Chapter - ${i}`,
                        disabled: usedNumbers.has(i)
                    });
                }

                setChapterOptions(options);

                // Select the first available chapter by default
                const firstAvailable = options.find(opt => !opt.disabled);
                if (firstAvailable) {
                    setSelectedChapter(firstAvailable);
                }
            }
        } catch (error) {
            console.error('Error fetching chapter numbers:', error);
            toast.error('Failed to load chapter numbers');
        }
    };

    const handleChapterSelect = (chapter) => {
        setSelectedChapter(chapter);
        setDropdownOpen(false);
    };
    const containsAlphanumeric = (text) => {
        const alphanumericRegex = /[a-zA-Z0-9]/; // Matches any letter or number
        return alphanumericRegex.test(text);
      };

      const handleTitleChange = (e) => {
        const value = e.target.value;
        
        // Validate for emojis and special characters
        if (containsInvalidCharacters(value)) {
            toast.error("Please enter valid text. Emojis and unsupported symbols are not allowed.", {
                toastId: 'emoji-error' // Prevents duplicate toasts
            });
            return; // Don't update the state with invalid characters
        }
        
        // NEW VALIDATION: Check if title contains at least one alphanumeric character
        if (value.trim() !== '' && !containsAlphanumeric(value)) {
            toast.error("Please enter a valid Chapter Title using letters or numbers.", {
                toastId: 'alphanumeric-error'
            });
            return;
        }
        
        setChapterTitle(value);
    };
    const handleMultilingualTitleChange = (setter) => (e) => {
        const value = e.target.value;
        if (containsInvalidCharacters(value)) {
            toast.error("Please enter valid text. Emojis and unsupported symbols are not allowed.", { toastId: 'emoji-error' });
            return;
        }
        setter(value);
    };

    const handleSubmit = async () => {
        const titleValidations = [
            { value: chapterTitle, lang: 'English' },
            { value: chapterTitleMar, lang: 'Marathi' },
            { value: chapterTitleHin, lang: 'Hindi' },
            { value: chapterTitleGuj, lang: 'Gujarati' },
        ];
        for (const { value, lang } of titleValidations) {
            if (!value.trim()) {
                toast.error(`${lang} chapter title is required`);
                return;
            }
        }

        if (!selectedChapter) {
            toast.error('Please select a chapter number');
            return;
        }
    
        try {
            setIsSubmitting(true);
    
            // Create chapter data object
            const chapterData = {
                user_id: currentId,
                course_id: courseId,
                title: chapterTitle,
                title_mar: chapterTitleMar || null,
                title_hin: chapterTitleHin || null,
                title_guj: chapterTitleGuj || null,
                status: status,
                sequence_number: selectedChapter.value
            };
    
            // Make API call
            const response = await _axios.post(`${ServerPath}/chapter/create`, chapterData);
    
            if (response.data.success) {
                toast.success('Chapter has been successfully created');
                onHide();
            }
        } catch (error) {
            console.error('Error creating chapter:', error);
            // Display the specific message from the API if available
            toast.error(error.response?.data?.message || 'Failed to create chapter');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Render a chapter option
    const renderChapterOption = (chapter) => (
        <div
            key={chapter.value}
            className={`chapter-option ${chapter.disabled ? 'disabled' : ''}`}
            onClick={() => !chapter.disabled && handleChapterSelect(chapter)}
            style={{
                padding: '8px 16px',
                cursor: chapter.disabled ? 'not-allowed' : 'pointer',
                backgroundColor: selectedChapter?.value === chapter.value ? '#EBF2FF' : 'white',
                opacity: chapter.disabled ? 0.5 : 1
            }}
        >
            {chapter.label}
        </div>
    );
    const handleStatusChange = (e) => {
        setStatus(e.target.value);
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            className="create-chapter-modal"
            size="xl"
        >
            <Modal.Header closeButton style={{ borderBottom: '1px solid #E2E8F0' }}>
                <Modal.Title>Create Chapter</Modal.Title>
            </Modal.Header>

            <Modal.Body className="px-4 py-3">
                <div className="d-flex justify-content-end align-items-center">
                    <span className="text-muted me-2">Last Updated Date -</span>
                    <span className="text-muted">NA</span>
                </div>

                <div className="mt-3">
                    <h6 className="text-primary mb-4" style={{ color: '#6366F1' }}>Chapter</h6>

                    <div className="row mb-3">
                        <div className="col-md-3">
                            <Form.Group>
                                <Form.Label style={{ fontWeight: '500' }}>Chapter Number</Form.Label>
                                <div className="position-relative">
                                    <div
                                        className="form-control d-flex justify-content-between align-items-center"
                                        onClick={() => setDropdownOpen(!dropdownOpen)}
                                        style={{ cursor: 'pointer', border: '1px solid #E2E8F0', backgroundColor: 'white' }}
                                    >
                                        <span>{selectedChapter ? selectedChapter.label : 'Select chapter number'}</span>
                                        <i className={`bi bi-chevron-${dropdownOpen ? 'up' : 'down'}`}></i>
                                    </div>
                                    {dropdownOpen && (
                                        <div
                                            className="position-absolute w-100 border border-1 rounded mt-1 bg-white z-10"
                                            style={{ maxHeight: '240px', overflowY: 'auto', zIndex: 1000, boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)' }}
                                        >
                                            {chapterOptions.map(renderChapterOption)}
                                        </div>
                                    )}
                                </div>
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group>
                                <Form.Label style={{ fontWeight: '500' }}>
                                    Chapter Status <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    value={status}
                                    onChange={handleStatusChange}
                                    style={{ border: '1px solid #E2E8F0', backgroundColor: 'white', height: '40px' }}
                                >
                                    <option value="New">New</option>
                                    <option value="Active">Active</option>
                                    <option value="Archived">Archived</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </div>

                    {/* Chapter Titles - 4 languages */}
                    <div className="row mb-3">
                        {[
                            { label: 'English', value: chapterTitle, onChange: handleTitleChange },
                            { label: 'Marathi', value: chapterTitleMar, onChange: handleMultilingualTitleChange(setChapterTitleMar) },
                            { label: 'Hindi', value: chapterTitleHin, onChange: handleMultilingualTitleChange(setChapterTitleHin) },
                            { label: 'Gujarati', value: chapterTitleGuj, onChange: handleMultilingualTitleChange(setChapterTitleGuj) },
                        ].map(lang => (
                            <div key={lang.label} className="col-3">
                                <Form.Group>
                                    <Form.Label style={{ fontWeight: '500', fontSize: '14px' }}>
                                        {lang.label} Title <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder={`Enter ${lang.label} title`}
                                        value={lang.value}
                                        onChange={lang.onChange}
                                        style={{ border: '1px solid #E2E8F0', backgroundColor: 'white' }}
                                    />
                                </Form.Group>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer style={{ borderTop: '1px solid #E2E8F0', justifyContent: 'flex-end' }}>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    style={{ backgroundColor: '#6366F1', minWidth: '80px' }}
                >
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateChapter;